import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import type { CatalogCard } from '@/types/card'
import { useApp } from '@/store/app'
import { useSearch, useSearchResults, type SearchStatus } from '@/features/search/useSearch'
import type { SearchHit } from '@/features/search/engine'
import {
  FACET_KINDS,
  FACET_KIND_LABELS,
  baueFacetten,
  facetKey,
  gesetzteFacetten,
  hatFilter,
  schalteFacette,
  schlageFacettenVor,
  type Facet,
  type FacetIndex,
  type SearchFilters,
} from '@/features/search/facetten'
import { useTastaturhoehe } from '@/lib/useTastaturhoehe'
import { CardRow } from '@/components/CardRow'
import { Chip, Empty, Label, Spinner } from '@/components/primitives'
import { IconClose, IconSearch } from '@/components/icons'

/**
 * Die Suche — ein Blatt, das über der Ansicht aufgeht, aus der es gerufen wurde.
 *
 * **Warum kein eigener Ort mehr.** Suchen heißt: kurz etwas nachschlagen und dorthin
 * zurückkehren, wo man war. Als vierter Reiter in der Navigation hat die Suche das
 * Gegenteil erzwungen — der Katalog, den man gerade durchgeblättert hat, war weg. Jetzt
 * bleibt er dahinter stehen.
 *
 * **Warum das Feld unten sitzt.** Aus demselben Grund wie die Navigationsleiste: Im
 * Einsatz wird einhändig bedient, und der Daumen erreicht die untere Hälfte. Die
 * Vorschläge stehen unmittelbar darüber — vom Tippen zum Antippen ist es so der
 * kürzeste Weg. Die Treffer wachsen darüber nach oben weg.
 *
 * **Warum Kategorie und Schlagwort mitsuchen.** Wer „gefahr" tippt, meint oft nicht ein
 * Wort im Fließtext, sondern ein Gebiet. Die Vorschläge machen daraus einen Filter, den
 * man antippt, statt ihn erst in einer zweiten Leiste zu suchen. Text und Filter wirken
 * zusammen, und ein Filter allein genügt auch ohne jedes Stichwort.
 */
export function Suche() {
  const navigate = useNavigate()
  const location = useLocation()
  const [params, setParams] = useSearchParams()
  const query = params.get('q') ?? ''

  const catalog = useApp((s) => s.catalog)
  const searchData = useApp((s) => s.searchData)
  const { search, status } = useSearch(catalog?.cards ?? null, searchData)

  const [filters, setFilters] = useState<SearchFilters>({})
  const { hits, searching } = useSearchResults(search, query, filters, status)

  const tastatur = useTastaturhoehe()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Alle Facetten des Bestands, einmal je Katalog. Der Vergleich beim Tippen läuft über
  // ein paar Dutzend Bezeichnungen und braucht deshalb weder Index noch Worker.
  const facettenIndex = useMemo(() => (catalog ? baueFacetten(catalog) : []), [catalog])
  const vorschlaege = useMemo(
    () => (catalog ? schlageFacettenVor(facettenIndex, catalog.cards, query, filters) : []),
    [facettenIndex, catalog, query, filters],
  )
  const gesetzt = useMemo(() => gesetzteFacetten(facettenIndex, filters), [facettenIndex, filters])
  const nachId = useMemo(
    () => new Map((catalog?.cards ?? []).map((card) => [card.id, card])),
    [catalog],
  )

  useEffect(() => {
    const dialog = dialogRef.current
    dialog?.showModal()
    // Direkt ins Feld — ein Tipp weniger, und die Tastatur steht schon, wenn das Blatt
    // fertig aufgegangen ist.
    inputRef.current?.focus()
    return () => dialog?.close()
  }, [])

  // `location.state` ist von Haus aus untypisiert. Einmal hier festgelegt, was die App
  // dort ablegt — der Rest der Datei arbeitet mit einem bekannten Wert.
  const zustand = (location.state ?? null) as { hintergrund?: unknown } | null

  const schliessen = () => {
    // Wurde das Blatt aus einer Ansicht heraus geöffnet, führt der Weg dorthin zurück —
    // samt Scrollposition und aufgeklappten Kategorien. Bei einem Direktaufruf
    // (Einsprungpunkt am App-Symbol, geteilter Link) gibt es nichts, wohin zurück:
    // dann zur Startseite.
    if (zustand?.hintergrund) void navigate(-1)
    else void navigate('/', { replace: true })
  }

  const setQuery = (value: string) => {
    // Der Zustand muss mitwandern: Ohne ihn verlöre die nächste Adresse den Hinweis auf
    // den Hintergrund, und das Blatt fiele beim ersten Tastendruck auf eine
    // bildschirmfüllende Ansicht zurück.
    setParams(value ? { q: value } : {}, { replace: true, state: zustand })
  }

  const umschalten = (facet: Facet) => {
    const warGesetzt = gesetzt.some((eintrag) => facetKey(eintrag) === facetKey(facet))
    setFilters((current) => schalteFacette(current, facet))
    // Wird ein Vorschlag angenommen, hat der getippte Text seinen Zweck erfüllt: Er
    // sollte die Facette finden, nicht zusätzlich im Volltext vorkommen. Bliebe er
    // stehen, suchte „gefahr" anschließend innerhalb der Gefahrgut-Karten nach dem Wort
    // „gefahr" — und läge damit neben dem, was gemeint war. Beim Entfernen bleibt das
    // Feld unberührt; dort hat der Text nichts mit der Facette zu tun.
    if (!warGesetzt) setQuery('')
    inputRef.current?.focus()
  }

  const suchbegriff = query.trim()
  const etwasGefragt = suchbegriff.length > 0 || hatFilter(filters)

  return (
    <dialog
      ref={dialogRef}
      aria-label="Taschenkarten durchsuchen"
      onCancel={(event) => {
        // Erst entscheidet die App, dann verschwindet der Dialog — sonst bliebe er beim
        // nächsten Rendern als geschlossenes Element stehen.
        event.preventDefault()
        schliessen()
      }}
      className="m-0 h-full max-h-none w-full max-w-none bg-transparent p-0
                 backdrop:bg-[rgb(0_0_0/0.55)] backdrop:[animation:grund-auf_200ms_ease-out]"
    >
      <div
        className="flex h-full flex-col justify-end px-3"
        style={{
          // Bei offener Tastatur rückt das Blatt genau um deren Höhe nach oben. Ohne das
          // läge das Feld dahinter — siehe useTastaturhoehe.
          paddingBottom: `calc(${tastatur}px + var(--nav-luft) + env(safe-area-inset-bottom))`,
          paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)',
        }}
        // Ein Tipp neben das Blatt schließt es. Nur das Element selbst, nicht seine
        // Kinder — sonst schlösse auch jeder Tipp in die Trefferliste.
        onClick={(event) => {
          if (event.target === event.currentTarget) schliessen()
        }}
      >
        <div
          className="glas glas-blatt mx-auto flex max-h-full w-full max-w-2xl flex-col
                     overflow-hidden rounded-card"
          style={{
            // Aus der Ecke, in der der Suchknopf sitzt — das Blatt soll erkennbar
            // dasselbe Element sein, nur aufgeklappt.
            transformOrigin: 'bottom right',
            animation: 'blatt-auf 200ms cubic-bezier(0.2, 0.7, 0.3, 1)',
          }}
        >
          <Trefferbereich
            status={status}
            searching={searching}
            hits={hits}
            nachId={nachId}
            suchbegriff={suchbegriff}
            etwasGefragt={etwasGefragt}
            filterGesetzt={hatFilter(filters)}
            facettenIndex={facettenIndex}
            onWaehlen={umschalten}
          />

          {vorschlaege.length > 0 && (
            <Vorschlaege vorschlaege={vorschlaege} onWaehlen={umschalten} />
          )}

          {/* Feld und Schließen-Knopf liegen nebeneinander, nicht ineinander: Das Feld
              wächst mit jedem Plättchen und bricht um; ein Knopf darin würde dabei in
              die zweite Zeile rutschen und wäre je nach Eingabe woanders. */}
          <div className="flex items-center gap-1 border-t border-line bg-bg-deep p-2">
            <div
              className="flex flex-1 flex-wrap items-center gap-1.5 rounded-card
                         border border-line-strong bg-field px-2 py-2 focus-within:border-accent"
            >
              <IconSearch className="ml-1 size-5 shrink-0 text-muted" />

              {gesetzt.map((facet) => (
                <FilterPlaettchen key={facetKey(facet)} facet={facet} onEntfernen={umschalten} />
              ))}

              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  // Rückschritt im leeren Feld nimmt den zuletzt gesetzten Filter weg —
                  // dasselbe Verhalten wie in jedem Adressfeld mit Empfängerplättchen.
                  if (event.key === 'Backspace' && query.length === 0 && gesetzt.length > 0) {
                    event.preventDefault()
                    umschalten(gesetzt[gesetzt.length - 1]!)
                  }
                }}
                // Kurz, sobald ein Plättchen daneben steht: Der lange Hinweis passt dann
                // nicht mehr in die Zeile und würde mitten im Wort abgeschnitten.
                placeholder={gesetzt.length > 0 ? 'Stichwort …' : 'Stichwort, Abkürzung oder Inhalt'}
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                aria-label="Taschenkarten durchsuchen"
                className="h-11 min-w-24 flex-1 bg-transparent px-1 text-base text-ink
                           placeholder:text-muted focus:outline-none
                           [&::-webkit-search-cancel-button]:hidden"
              />
            </div>

            {/* Ein einziger Knopf, immer an derselben Stelle und immer mit derselben
                Bedeutung. Ein zweites Kreuz zum Leeren des Feldes stünde unmittelbar
                daneben und wäre im Einsatz nicht auseinanderzuhalten — Text löscht die
                Tastatur, Filter nimmt das Plättchen selbst zurück. */}
            <button
              type="button"
              onClick={schliessen}
              aria-label="Suche schließen"
              className="tap flex shrink-0 items-center justify-center text-muted hover:text-ink"
            >
              <IconClose className="size-5" />
            </button>
          </div>
        </div>
      </div>
    </dialog>
  )
}

/**
 * Filtervorschläge, nach Art gruppiert.
 *
 * Die Art steht als Wort in der Zeile und nicht als Überschrift darüber: Bei zwei bis
 * drei Zeilen kostet eine eigene Überschriftszeile mehr Platz, als sie an Klarheit
 * bringt. Ausgeschrieben wird sie nur in der ersten Zeile ihrer Gruppe.
 */
function Vorschlaege({
  vorschlaege,
  onWaehlen,
}: {
  vorschlaege: readonly Facet[]
  onWaehlen: (facet: Facet) => void
}) {
  return (
    <ul className="border-t border-line">
      {FACET_KINDS.flatMap((kind) =>
        vorschlaege
          .filter((facet) => facet.kind === kind)
          .map((facet, index) => (
            <li key={facetKey(facet)}>
              <button
                type="button"
                onClick={() => onWaehlen(facet)}
                className="flex min-h-12 w-full items-center gap-3 px-4 text-left
                           transition-colors duration-100 hover:bg-raised"
              >
                <span
                  aria-hidden
                  className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wider text-muted"
                >
                  {index === 0 ? FACET_KIND_LABELS[kind] : ''}
                </span>
                <span className="flex-1 text-base font-semibold text-ink-strong">
                  {facet.label}
                </span>
                <span className="sr-only">als Filter {FACET_KIND_LABELS[kind]} setzen</span>
                <span aria-hidden className="text-lg font-bold text-accent">
                  +
                </span>
              </button>
            </li>
          )),
      )}
    </ul>
  )
}

/** Ein gesetzter Filter im Suchfeld. Antippen nimmt ihn wieder heraus. */
function FilterPlaettchen({
  facet,
  onEntfernen,
}: {
  facet: Facet
  onEntfernen: (facet: Facet) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onEntfernen(facet)}
      className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-chip bg-accent px-3
                 text-sm font-semibold text-accent-ink"
    >
      <span className="auf-akzent">{facet.label}</span>
      <span className="sr-only">— Filter {FACET_KIND_LABELS[facet.kind]} entfernen</span>
      <IconClose className="size-4" />
    </button>
  )
}

/** Trefferbereich über den Vorschlägen. Wächst nach oben und scrollt für sich. */
function Trefferbereich({
  status,
  searching,
  hits,
  nachId,
  suchbegriff,
  etwasGefragt,
  filterGesetzt,
  facettenIndex,
  onWaehlen,
}: {
  status: SearchStatus
  searching: boolean
  hits: readonly SearchHit[]
  nachId: ReadonlyMap<string, CatalogCard>
  suchbegriff: string
  etwasGefragt: boolean
  filterGesetzt: boolean
  facettenIndex: readonly FacetIndex[]
  onWaehlen: (facet: Facet) => void
}) {
  const zaehler =
    etwasGefragt && !searching && hits.length > 0 ? `${hits.length} Treffer` : ''

  return (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {/* Die Trefferzahl wird angesagt, sobald sie sich ändert: Wer das Blatt nicht
          sieht, erfährt so beim Tippen, ob sich die Eingabe lohnt. Das Element bleibt
          auch leer stehen — ein Bereich, der erst entsteht, wenn er etwas zu sagen hat,
          wird von der Sprachausgabe nicht mehr beobachtet. */}
      <p
        role="status"
        aria-live="polite"
        className={zaehler ? 'px-4 pb-2 pt-3 text-xs text-muted' : 'sr-only'}
      >
        {zaehler}
      </p>

      <div className="px-4 pb-3 pt-3 empty:hidden">
        {!etwasGefragt ? (
          <Einstieg facettenIndex={facettenIndex} onWaehlen={onWaehlen} />
        ) : status === 'fehler' ? (
          <Empty
            title="Suche nicht verfügbar"
            hint="Der Suchindex konnte nicht geladen werden. Über den Katalog sind alle Karten weiterhin erreichbar."
          />
        ) : searching && hits.length === 0 ? (
          <Spinner label={status === 'lädt' ? 'Suchindex wird geladen …' : 'Suche läuft …'} />
        ) : hits.length === 0 ? (
          <Empty
            title={suchbegriff ? `Keine Karte zu „${suchbegriff}"` : 'Keine Karte dazu'}
            hint={
              filterGesetzt
                ? 'Die gesetzten Filter schränken das Ergebnis ein. Ein Plättchen antippen nimmt es wieder heraus.'
                : 'Anderes Stichwort versuchen — auch Abkürzungen wie „PA" oder „MANV" werden gefunden.'
            }
          />
        ) : (
          <div className="-mx-4 border-t border-line">
            {hits.map((hit) => {
              const card = nachId.get(hit.id)
              if (!card) return null
              return (
                <CardRow
                  key={hit.id}
                  card={card}
                  snippet={hit.snippet}
                  highlights={hit.highlights}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Was zu sehen ist, bevor etwas eingegeben wurde.
 *
 * Kein bloßer Hinweistext: Wer die Suche öffnet, ohne zu wissen, wonach er fragen soll,
 * kommt mit einem Gebiet weiter als mit der Aufforderung, ein Stichwort zu tippen. Ein
 * Tipp genügt, und die Trefferliste steht — ganz ohne Tastatur.
 */
function Einstieg({
  facettenIndex,
  onWaehlen,
}: {
  facettenIndex: readonly FacetIndex[]
  onWaehlen: (facet: Facet) => void
}) {
  const gruppen = useMemo(() => {
    const nachArt = (kind: Facet['kind']) =>
      facettenIndex.filter((eintrag) => eintrag.facet.kind === kind).map((eintrag) => eintrag.facet)

    return [
      { kind: 'kategorie' as const, titel: 'Gebiet', facetten: nachArt('kategorie') },
      { kind: 'organisation' as const, titel: 'Organisation', facetten: nachArt('organisation') },
    ]
  }, [facettenIndex])

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Die Suche findet Titel, Schlagwörter und den gesamten Text der Karten — auch ohne
        Umlaute und bei Vertippern.
      </p>
      {gruppen.map(({ kind, titel, facetten }) => {
        if (facetten.length === 0) return null
        return (
          <div key={kind}>
            <Label>{titel}</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {facetten.map((facet) => (
                <Chip
                  key={facetKey(facet)}
                  active={false}
                  onClick={() => onWaehlen(facet)}
                  // Kennfarbe nur bei Organisationen. Bedingt hineingereicht statt als
                  // undefined: Das Projekt führt optionale Eigenschaften streng, ein
                  // ausdrücklich gesetztes undefined gilt dort nicht als „nicht gesetzt".
                  {...(facet.kind === 'organisation'
                    ? { swatch: `var(--org-${facet.id})` }
                    : {})}
                >
                  {facet.label}
                </Chip>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
